// src/App.js
import React, { useState, useEffect } from 'react';
import { StructureProvider } from './contexts/StructureContext';
import { PersonProvider } from './contexts/PersonContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import StockManagementApp from './StockManagementApp';
import AdminPage from './pages/AdminPage';
import Dashboard from './components/Dashboard';
import OrderManagement from './components/OrderManagement';
import MilkTraceability from './components/MilkTraceability';
import MedTraceability from './components/MedTraceability';
import WaterTraceability from './components/WaterTraceability';
import EquipmentPage from './pages/EquipmentPage';
import MaintenancePage from './pages/MaintenancePage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import TasksPage from './pages/TasksPage';
import HRPage from './pages/HRPage';
import FinancialDashboardPage from './pages/FinancialDashboardPage';
import TreasuryPage from './pages/TreasuryPage';
import CrechePage from './pages/CrechePage';
import TimesheetPage from './pages/TimesheetPage';
import CalendarPage from './pages/CalendarPage';
import CommunicationPage from './pages/CommunicationPage';
import DiapersPage from './pages/DiapersPage';
import MealsPage from './pages/MealsPage';
import RecruitmentPage from './pages/RecruitmentPage';
import AppLayout from './components/AppLayout';
import Login from './Login';
import { authService } from './auth-service';
import { ImpersonateProvider, useImpersonate } from './contexts/ImpersonateContext';
import PWAPrompt from './components/PWAPrompt';
import LoadingSpinner from './components/LoadingSpinner';
import ChangelogModal from './components/ChangelogModal';
import SettingsModal from './components/SettingsModal';
import PortalRH from './pages/PortalRH';
import { usePWA } from './hooks/usePWA';

// P5-B : portail candidat public — bypass complet du flow OAuth/Authentik
const IS_PORTAL_RH = typeof window !== 'undefined' && /^\/portal-rh\//.test(window.location.pathname);

// Wrapper sans hooks : route soit vers PortalRH (public) soit vers AppMain (admin SPA).
// Indispensable pour respecter les règles des Hooks React (pas d'early-return AVANT les hooks).
function App() {
  if (IS_PORTAL_RH) {
    return (
      <NotificationProvider>
        <PortalRH />
      </NotificationProvider>
    );
  }
  return <AppMain />;
}

function AppMain() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('stocks');
  const [showChangelog, setShowChangelog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { subscribeToPush, notificationPermission } = usePWA();

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      if (currentUser?.defaultModule) {
        setCurrentView(currentUser.defaultModule);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const autoSubscribe = async () => {
      if (user && notificationPermission === 'granted') {
        try {
          await subscribeToPush();
        } catch (error) {
          // Erreur silencieuse - non critique
        }
      }
    };
    autoSubscribe();
  }, [user, notificationPermission, subscribeToPush]);

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setCurrentView('stocks');
  };

  // payload optionnel (ex: { employeeId, childId, productId... }) — consommé par les pages
  // enfants pour ouvrir directement une fiche. Auto-reset après consommation.
  const [navPayload, setNavPayload] = useState(null);

  const handleNavigate = (view, payload) => {
    // Prevent non-admin from accessing admin page
    if (view === 'admin' && user?.role !== 'admin') return;
    setCurrentView(view);
    setNavPayload(payload || null);
  };

  // Callback que les pages enfants appellent quand elles ont consommé le payload
  const consumeNavPayload = () => setNavPayload(null);

  if (loading) {
    return <LoadingSpinner size="large" text="Vérification de l'authentification..." fullScreen />;
  }

  if (!user) {
    return (
      <ThemeProvider>
        <Login onLoginSuccess={handleLoginSuccess} />
      </ThemeProvider>
    );
  }

  const renderContent = (effectiveUser) => ({ showAllStructures, setShowAllStructures }) => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'orders':
        return <OrderManagement showAllStructures={showAllStructures} />;
      case 'admin':
        // Admin page toujours accessible via le vrai user (pas l'impersonation)
        return user.role === 'admin' ? <AdminPage /> : null;
      case 'milk':
        return <MilkTraceability isAdmin={effectiveUser?.role === 'admin'} canEdit={effectiveUser?.role === 'admin' || effectiveUser?.role === 'structure_admin'} showAllStructures={showAllStructures} />;
      case 'medications':
        return <MedTraceability isAdmin={effectiveUser?.role === 'admin'} canEdit={effectiveUser?.role === 'admin' || effectiveUser?.role === 'structure_admin'} showAllStructures={showAllStructures} />;
      case 'water':
        return <WaterTraceability isAdmin={effectiveUser?.role === 'admin'} canEdit={effectiveUser?.role === 'admin' || effectiveUser?.role === 'structure_admin'} showAllStructures={showAllStructures} />;
      case 'equipment':
        return <EquipmentPage showAllStructures={showAllStructures} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'maintenance':
        return <MaintenancePage showAllStructures={showAllStructures} />;
      case 'workOrders':
        return <WorkOrdersPage showAllStructures={showAllStructures} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'tasks':
        return <TasksPage userRole={effectiveUser?.role} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'calendar':
        return <CalendarPage user={effectiveUser} showAllStructures={showAllStructures} />;
      case 'hr':
        return <HRPage showAllStructures={showAllStructures} initialTab="employees" onNavigate={handleNavigate} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'hr-dashboard':
        return <HRPage showAllStructures={showAllStructures} initialTab="dashboard" onNavigate={handleNavigate} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'hr-dossiers':
        return <HRPage showAllStructures={showAllStructures} initialTab="dossiers" onNavigate={handleNavigate} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'hr-transport-collection':
        return <HRPage showAllStructures={showAllStructures} initialTab="transport-collection" onNavigate={handleNavigate} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'hr-document-templates':
        return <HRPage showAllStructures={showAllStructures} initialTab="document-templates" onNavigate={handleNavigate} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'recruitment':
        return <RecruitmentPage showAllStructures={showAllStructures} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'financial':
        return effectiveUser.role === 'admin' ? <FinancialDashboardPage showAllStructures={showAllStructures} /> : null;
      case 'treasury':
        return effectiveUser.role === 'admin' ? <TreasuryPage showAllStructures={showAllStructures} /> : null;
      case 'creche':
        return <CrechePage user={effectiveUser} showAllStructures={showAllStructures} navPayload={navPayload} onNavPayloadConsumed={consumeNavPayload} />;
      case 'communication':
        return <CommunicationPage user={effectiveUser} showAllStructures={showAllStructures} />;
      case 'diapers':
        return <DiapersPage showAllStructures={showAllStructures} />;
      case 'meals':
        return <MealsPage showAllStructures={showAllStructures} />;
      case 'timesheets':
        return <TimesheetPage user={effectiveUser} showAllStructures={showAllStructures} onNavigate={handleNavigate} />;
      case 'stocks':
      default:
        return (
          <StockManagementApp
            user={effectiveUser}
            onLogout={handleLogout}
            showAllStructures={showAllStructures}
            setShowAllStructures={setShowAllStructures}
            navPayload={navPayload}
            onNavPayloadConsumed={consumeNavPayload}
          />
        );
    }
  };

  return (
    <ThemeProvider>
      <NotificationProvider>
      <ImpersonateProvider realUser={user}>
      <StructureProvider>
        <PersonProvider userName={user?.name}>
        <AuthenticatedApp
          currentView={currentView}
          handleNavigate={handleNavigate}
          handleLogout={handleLogout}
          renderContent={renderContent}
          showChangelog={showChangelog}
          setShowChangelog={setShowChangelog}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          user={user}
        />
        </PersonProvider>
      </StructureProvider>
      </ImpersonateProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

// Composant interne qui utilise useImpersonate (doit etre dans le Provider)
function AuthenticatedApp({
  currentView, handleNavigate, handleLogout, renderContent,
  showChangelog, setShowChangelog, showSettings, setShowSettings, user,
}) {
  const { effectiveUser } = useImpersonate();

  return (
    <>
      <AppLayout
        currentView={currentView}
        onNavigate={handleNavigate}
        user={effectiveUser}
        onLogout={handleLogout}
        onShowChangelog={() => setShowChangelog(true)}
        onShowSettings={() => setShowSettings(true)}
      >
        {renderContent(effectiveUser)}
      </AppLayout>
      {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} />}
      {showSettings && <SettingsModal user={user} onClose={() => setShowSettings(false)} />}
      <PWAPrompt />
    </>
  );
}

export default App;
